// src/pages/PesquisaJurisprudencia/index.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './PesquisaJurisprudencia.css';

import useFiltrosTab from './hooks/useFiltrosTab';
import useCasoTab from './hooks/useCasoTab';
import useIATab from './hooks/useIATab';
import FiltersTab from './components/FiltersTab';
import CasoTab from './components/CasoTab';
import IATab from './components/IATab';
import ResultadosSection from './components/ResultadosSection';
import JurisprudenciaModal from './components/JurisprudenciaModal';
import { API_BASE_URL, PAGINACAO_INICIAL } from './utils/constants';
import { formatarData, formatarNumero, abrirUrlOriginal } from './utils/helpers';

const ABAS = [
  { value: 'filtros', label: 'Pesquisa por Filtros' },
  { value: 'caso',   label: 'Pesquisa por Caso'   },
  { value: 'ia',     label: 'Pesquisa por IA'     }
];

const PesquisaJurisprudencia = () => {
  const navigate = useNavigate();

  // ─── Estado compartilhado entre todas as abas ─────────────────────────────
  const [abaAtiva, setAbaAtiva] = useState('filtros');
  const [resultados, setResultados] = useState([]);
  const [paginacao, setPaginacao] = useState(PAGINACAO_INICIAL);
  const [itemSelecionado, setItemSelecionado] = useState(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [buscaAtiva, setBuscaAtiva] = useState(false);

  // Ref para capturar abaAtiva atual dentro de callbacks estáveis
  const abaAtivaRef = useRef(abaAtiva);
  useEffect(() => { abaAtivaRef.current = abaAtiva; }, [abaAtiva]);

  // ─── Callbacks para hooks atualizarem estado compartilhado ────────────────
  const handleResultados = useCallback((data) => {
    setResultados(data);
    setBuscaAtiva(true);
  }, []);

  const handlePaginacao = useCallback((data) => {
    setPaginacao(data);
  }, []);

  // Chamado pelos hooks ao mudar parâmetros de busca (tribunal, caso)
  const handleResetBusca = useCallback(() => {
    setResultados([]);
    setPaginacao(PAGINACAO_INICIAL);
    setBuscaAtiva(false);
  }, []);

  // ─── Instância dos hooks de cada aba ──────────────────────────────────────
  const filtrosHook = useFiltrosTab({
    onResultados: handleResultados,
    onPaginacao:  handlePaginacao,
    onResetBusca: handleResetBusca
  });

  const casoHook = useCasoTab({
    onResultados: handleResultados,
    onPaginacao:  handlePaginacao,
    onResetBusca: handleResetBusca
  });

  const iaHook = useIATab({
    onResultados: handleResultados,
    onPaginacao:  handlePaginacao
  });

  // ─── Referências estáveis para o session storage ──────────────────────────
  const { getState: filtrosGetState, restoreState: filtrosRestoreState } = filtrosHook;
  const { getState: casoGetState,   restoreState: casoRestoreState   } = casoHook;

  // ─── Session Storage ───────────────────────────────────────────────────────
  const salvarEstadoAtual = useCallback(() => {
    const estado = {
      abaAtiva:   abaAtivaRef.current,
      resultados,
      paginacao,
      buscaAtiva,
      ...filtrosGetState(),
      ...casoGetState(),
      timestamp: Date.now()
    };
    sessionStorage.setItem('jurisprudenciaEstado', JSON.stringify(estado));
    console.log('💾 Estado da pesquisa salvo');
  }, [resultados, paginacao, buscaAtiva, filtrosGetState, casoGetState]);

  const restaurarEstadoSalvo = useCallback(() => {
    try {
      const raw = sessionStorage.getItem('jurisprudenciaEstado');
      if (!raw) return false;
      const estado = JSON.parse(raw);
      const TRINTA_MIN = 30 * 60 * 1000;
      if (Date.now() - estado.timestamp > TRINTA_MIN) {
        sessionStorage.removeItem('jurisprudenciaEstado');
        return false;
      }
      console.log('🔄 Restaurando estado da pesquisa');
      setAbaAtiva(estado.abaAtiva || 'filtros');
      setResultados(estado.resultados || []);
      setPaginacao(estado.paginacao || PAGINACAO_INICIAL);
      setBuscaAtiva(estado.buscaAtiva || false);
      filtrosRestoreState(estado);
      casoRestoreState(estado);
      sessionStorage.removeItem('jurisprudenciaEstado');
      return true;
    } catch (e) {
      console.error('Erro ao restaurar estado:', e);
      sessionStorage.removeItem('jurisprudenciaEstado');
      return false;
    }
  }, [filtrosRestoreState, casoRestoreState]);

  // ─── Inicialização ─────────────────────────────────────────────────────────
  useEffect(() => {
    restaurarEstadoSalvo();
    // Executar apenas na montagem do componente
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carregar casos ao ativar aba "caso"
  const { casos, loadingCasos, carregarCasos } = casoHook;
  useEffect(() => {
    if (abaAtiva === 'caso' && casos.length === 0 && !loadingCasos) {
      carregarCasos();
    }
  }, [abaAtiva, casos.length, loadingCasos, carregarCasos]);

  // ─── Mudança de aba ────────────────────────────────────────────────────────
  // Resultados são preservados ao trocar de aba (comportamento original)
  const handleAbaChange = useCallback((aba) => {
    setAbaAtiva(aba);
  }, []);

  // ─── Ver detalhes (busca por ID) ───────────────────────────────────────────
  const verMais = useCallback(async (id) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      filtrosHook.tribunaisSelecionados.forEach(t => params.append('tribunais', t));
      const response = await fetch(`${API_BASE_URL}/jurisprudencia/${id}?${params}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setItemSelecionado(data.data);
        setModalAberto(true);
      } else {
        alert('Erro ao carregar detalhes');
      }
    } catch (e) {
      console.error('Erro ao carregar detalhes:', e);
      alert('Erro ao carregar detalhes');
    }
  }, [filtrosHook.tribunaisSelecionados]);

  // ─── Navegação para Chat com IA ────────────────────────────────────────────
  const navegarParaChat = useCallback(() => {
    if (!itemSelecionado) return;
    salvarEstadoAtual();

    const aba = abaAtivaRef.current;
    let contexto = '';

    if (aba === 'caso' && casoHook.casoSelecionado) {
      const c = casoHook.casoSelecionado;
      const linhas = [
        '=== CONTEXTO DO CASO ===',
        `Nome do Caso: ${c.name || `Caso ${c.id}`}`,
        `Data de Criação: ${formatarData(c.created_at)}`,
        '',
        'DESCRIÇÃO DO CASO:',
        c.description || 'Nenhuma descrição disponível',
        ''
      ];
      if (c.analise_caso) {
        linhas.push(
          'ANÁLISE DO CASO:',
          typeof c.analise_caso === 'string' ? c.analise_caso : JSON.stringify(c.analise_caso, null, 2),
          ''
        );
      }
      linhas.push('=== JURISPRUDÊNCIA RELACIONADA ===', '');
      contexto = linhas.join('\n');
    }

    const camposJuris = [
      `ID: ${itemSelecionado.id}`,
      `URL Acordao: ${itemSelecionado.url_original}`,
      `Tribunal: ${itemSelecionado.tribunal_origem}`,
      `Processo: ${itemSelecionado.processo_numero || 'N/A'}`,
      `Relator: ${itemSelecionado.relator || 'N/A'}`,
      `Data do Acórdão: ${itemSelecionado.data_acordao || 'N/A'}`,
      `Meio Processual: ${itemSelecionado.meio_processual || 'N/A'}`,
      `Sumário: ${itemSelecionado.sumario || 'N/A'}`,
      `Decisão: ${itemSelecionado.decisao || 'N/A'}`,
      `Votação: ${itemSelecionado.votacao || 'N/A'}`,
      `Tags: ${itemSelecionado.tags ? itemSelecionado.tags.join(', ') : 'N/A'}`,
      `Palavras-chave: ${itemSelecionado.palavras_chave ? itemSelecionado.palavras_chave.join(', ') : 'N/A'}`,
      `Tema: ${itemSelecionado.tema_classificado || 'N/A'}`,
      `Texto Integral: ${itemSelecionado.texto_integral || 'N/A'}`
    ];

    navigate('/chat-with-ai', {
      state: {
        content: contexto + camposJuris.join('\n\n'),
        origin: 'pesquisa-jurisprudencia',
        originTitle: 'Pesquisa de Jurisprudência'
      }
    });
  }, [itemSelecionado, casoHook.casoSelecionado, salvarEstadoAtual, navigate]);

  // ─── Paginação (aba filtros) ───────────────────────────────────────────────
  const mudarPagina = useCallback((novaPagina) => {
    filtrosHook.pesquisar(novaPagina);
  }, [filtrosHook]);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="pesquisa-jurisprudencia">
      <h1>Pesquisa de Jurisprudência</h1>

      {/* Abas de navegação */}
      <div className="abas-container">
        {ABAS.map(aba => (
          <button
            key={aba.value}
            className={`aba ${abaAtiva === aba.value ? 'ativa' : ''}`}
            onClick={() => handleAbaChange(aba.value)}
          >
            {aba.label}
          </button>
        ))}
      </div>

      {/* Conteúdo da aba ativa */}
      {abaAtiva === 'filtros' && (
        <FiltersTab
          tribunaisSelecionados={filtrosHook.tribunaisSelecionados}
          tags={filtrosHook.tags}
          selectedTags={filtrosHook.selectedTags}
          textoFiltro={filtrosHook.textoFiltro}
          setTextoFiltro={filtrosHook.setTextoFiltro}
          loading={filtrosHook.loading}
          loadingTags={filtrosHook.loadingTags}
          modalTagsAberto={filtrosHook.modalTagsAberto}
          setModalTagsAberto={filtrosHook.setModalTagsAberto}
          estatisticas={filtrosHook.estatisticas}
          handleTribunalChange={filtrosHook.handleTribunalChange}
          handleTagsChange={filtrosHook.handleTagsChange}
          handleRemoveTag={filtrosHook.handleRemoveTag}
          pesquisar={filtrosHook.pesquisar}
          formatarData={formatarData}
          formatarNumero={formatarNumero}
        />
      )}

      {abaAtiva === 'caso' && (
        <CasoTab
          casos={casoHook.casos}
          casoSelecionado={casoHook.casoSelecionado}
          loadingCasos={casoHook.loadingCasos}
          loadingPesquisaCaso={casoHook.loadingPesquisaCaso}
          handleCasoChange={casoHook.handleCasoChange}
          encontrarJurisprudenciaPorCaso={casoHook.encontrarJurisprudenciaPorCaso}
          formatarData={formatarData}
        />
      )}

      {abaAtiva === 'ia' && (
        <IATab
          descricaoIA={iaHook.descricaoIA}
          setDescricaoIA={iaHook.setDescricaoIA}
          loadingIA={iaHook.loadingIA}
          encontrarJurisprudenciaPorIA={iaHook.encontrarJurisprudenciaPorIA}
        />
      )}

      {/* Resultados compartilhados entre todas as abas */}
      <ResultadosSection
        resultados={resultados}
        paginacao={paginacao}
        abaAtiva={abaAtiva}
        casoSelecionado={casoHook.casoSelecionado}
        loading={filtrosHook.loading}
        loadingPesquisaCaso={casoHook.loadingPesquisaCaso}
        loadingIA={iaHook.loadingIA}
        buscaAtiva={buscaAtiva}
        tribunaisSelecionados={filtrosHook.tribunaisSelecionados}
        selectedTags={filtrosHook.selectedTags}
        textoFiltro={filtrosHook.textoFiltro}
        descricaoIA={iaHook.descricaoIA}
        onVerMais={verMais}
        onMudarPagina={mudarPagina}
        formatarData={formatarData}
        formatarNumero={formatarNumero}
        abrirUrlOriginal={abrirUrlOriginal}
      />

      {/* Modal de detalhes */}
      <JurisprudenciaModal
        item={itemSelecionado}
        isOpen={modalAberto}
        onClose={() => setModalAberto(false)}
        abaAtiva={abaAtiva}
        casoSelecionado={casoHook.casoSelecionado}
        loadingAssociar={casoHook.loadingAssociar}
        onAssociar={casoHook.associarJurisprudenciaAoCaso}
        onNavegateChat={navegarParaChat}
        formatarData={formatarData}
        abrirUrlOriginal={abrirUrlOriginal}
      />
    </div>
  );
};

export default PesquisaJurisprudencia;